import { ReactElement, useEffect, useRef, type ReactNode } from 'react';

import styles from './modal.module.scss';
import { Xmark } from '@unified-checkout/design-system/icons/index';
import { TModalState } from 'libs/design-system/src/types/modal';
import { ModalHeader } from 'libs/design-system/src/components/Modal/ModalHeader';
import { useClickOutside } from 'libs/design-system/src/hooks/useClickOutside';

type IModalProps = {
  lang?: 'en' | 'ar';
  state?: TModalState;
  open: boolean;
  width?: string;
  maxWidth?: string;
  isMobile?: boolean;
  hasCloseIcon?: boolean;
  onCloseHandler?: () => void;
  closeOnClickOutside?: boolean;
  headerIcon?: ReactElement<'svg'>;
  title: string;
  supportingText?: string;
  loading?: boolean;
  keepHeaderIconDefaultColors?: boolean;
  children?: ReactNode;
};
export const RefundModal = (props: IModalProps) => {
  const {
    state = 'default',
    open,
    width = '35rem',
    maxWidth,
    isMobile = false,
    hasCloseIcon,
    onCloseHandler,
    closeOnClickOutside = true,
    headerIcon,
    title,
    supportingText,
    keepHeaderIconDefaultColors = false,
    children,
  } = props;

  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef?.current;

    if (open) {
      openDialogHandler();
      dialog?.addEventListener('keydown', handleKeydown);
    } else {
      closeDialogHandler();
      dialog?.removeEventListener('keydown', handleKeydown);
    }
  }, [open]);

  const openDialogHandler = () => {
    dialogRef.current?.showModal();
    document.documentElement.style.overflow = 'hidden';
  };

  const closeDialogHandler = () => {
    dialogRef.current?.close();
    document.documentElement.style.overflow = '';
    onCloseHandler?.();
  };

  const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      event.preventDefault();
      onCloseHandler?.();
    }
  };

  closeOnClickOutside &&
    onCloseHandler &&
    useClickOutside(dialogRef, onCloseHandler, open, true);

  return (
    <section>
      <dialog
        ref={dialogRef}
        data-modal-is-mobile={isMobile}
        className={styles['modal']}
        style={{ maxWidth, width }}
      >
        {hasCloseIcon && (
          <button
            className={styles['modal__close-icon']}
            onClick={onCloseHandler}
          >
            {Xmark}
          </button>
        )}

        <ModalHeader
          state={state}
          headerIcon={headerIcon}
          title={title}
          supportingText={supportingText}
          keepIconDefaultColors={keepHeaderIconDefaultColors}
        />
        {children}
      </dialog>
    </section>
  );
};
